import {
  evaluate,
  unit,
  number as mathNumber,
  abs,
  round,
  floor,
  ceil,
  min,
  max,
  sqrt,
  pow,
  exp,
  log as mathLog,
  sin,
  cos,
  tan,
  pi,
  e,
} from 'mathjs';
import {
  FormulaScope as IFormulaScope,
  RecipeGroupState,
  RecipeRowState,
  RecipeState,
  RecipeColumnState,
  Source,
} from './types';
import { log } from '@/lib/utils/logger';

export class FormulaScope implements IFormulaScope {
  public const: Record<string, any> = {};
  private getRecipeState: () => RecipeState;
  private recipeGroup?: RecipeGroupState;
  private recipeRow?: RecipeRowState;
  private uploadData?: any[];
  private runtimeFormula?: string;
  private dependencyCache: Map<string, boolean> = new Map();

  constructor(
    getRecipeState: () => RecipeState,
    formulaConstants?: Record<string, any> | any[],
    runtimeFormula?: string
  ) {
    if (Array.isArray(formulaConstants)) {
      this.uploadData = formulaConstants;
    } else {
      this.const = formulaConstants || {};
    }
    this.getRecipeState = getRecipeState;
    this.runtimeFormula = runtimeFormula;
  }

  private get recipe(): RecipeState {
    return this.getRecipeState();
  }

  /**
   * Build dependency cache for all columns in the current group
   */
  private buildDependencyCache(): void {
    if (!this.recipeGroup) return;

    this.dependencyCache.clear();

    // Get all column symbols in this group
    const allColumnSymbols = this.recipeGroup.columns.map((col) => col.symbol).filter(Boolean);

    // Check each column against all formulas
    for (const columnSymbol of allColumnSymbols) {
      const isReferenced = this.checkColumnInAllFormulas(columnSymbol);
      this.dependencyCache.set(columnSymbol, isReferenced);
    }
  }

  /**
   * Check if a column is referenced in any formula (cached version)
   */
  public isColumnReferencedInFormulas(columnSymbol: string): boolean {
    if (!this.recipeGroup) return false;

    // Use cached result if available
    if (this.dependencyCache.has(columnSymbol)) {
      return this.dependencyCache.get(columnSymbol)!;
    }

    // Fallback to real-time check (shouldn't happen if cache is built properly)
    log.warn(`[FormulaScope] No cached dependency for ${columnSymbol}, falling back to real-time check`);
    return this.checkColumnInAllFormulas(columnSymbol);
  }

  /**
   * Check if a column is referenced in any formula (real-time version)
   */
  private checkColumnInAllFormulas(columnSymbol: string): boolean {
    if (!this.recipeGroup) return false;

    // Check all formula columns in the current group
    for (const column of this.recipeGroup.columns) {
      if (column.sourceType === Source.Formula || column.sourceType === Source.OverwrittableFormula) {
        if (column.source && this.isColumnReferencedInFormula(column.source, columnSymbol)) {
          return true;
        }
      }
    }

    // Check FormulaPerRow formulas in all rows
    for (const row of this.recipeGroup.rows) {
      if (row.formulas && Array.isArray(row.formulas)) {
        for (const [, formula] of row.formulas) {
          if (this.isColumnReferencedInFormula(formula, columnSymbol)) {
            return true;
          }
        }
      }
    }

    // Check runtime formula if available
    if (this.runtimeFormula && this.isColumnReferencedInFormula(this.runtimeFormula, columnSymbol)) {
      return true;
    }

    return false;
  }

  /**
   * Check if a column is referenced in a specific formula
   */
  public isColumnReferencedInFormula(formula: string | null | undefined, columnSymbol: string): boolean {
    if (!formula) {
      return false;
    }

    // Extract all cell() and group() function calls from the formula
    const cellMatches = formula.match(/cell\s*\(\s*['"`]?([^'"`,)]+)['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/g);
    const groupMatches = formula.match(/group\s*\(\s*['"`]?([^'"`,)]+)['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/g);

    // Check cell() references
    if (cellMatches) {
      for (const match of cellMatches) {
        const colMatch = match.match(/cell\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/);
        if (colMatch && colMatch[1] === columnSymbol) {
          return true;
        }
      }
    }

    // Check group() references
    if (groupMatches) {
      for (const match of groupMatches) {
        const colMatch = match.match(/group\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/);
        if (colMatch && colMatch[1] === columnSymbol) {
          return true;
        }
      }
    }

    // Check for direct variable references (for FormulaPerRow formulas like "T + 273")
    if (formula.includes(columnSymbol)) {
      return true;
    }

    return false;
  }

  /**
   * Get all column dependencies for a specific formula
   */
  public getFormulaDependencies(formula: string | null | undefined): string[] {
    const dependencies: string[] = [];

    if (!formula) {
      return dependencies;
    }

    // Extract cell() function calls
    const cellMatches = formula.match(/cell\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/g);
    if (cellMatches) {
      for (const match of cellMatches) {
        const colMatch = match.match(/cell\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/);
        if (colMatch && !dependencies.includes(colMatch[1])) {
          dependencies.push(colMatch[1]);
        }
      }
    }

    // Extract group() function calls
    const groupMatches = formula.match(/group\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/g);
    if (groupMatches) {
      for (const match of groupMatches) {
        const colMatch = match.match(/group\s*\(\s*['"`]?[^'"`,)]+['"`]?\s*,\s*['"`]?([^'"`,)]+)['"`]?\s*\)/);
        if (colMatch && !dependencies.includes(colMatch[1])) {
          dependencies.push(colMatch[1]);
        }
      }
    }

    return dependencies;
  }

  /**
   * Check for circular dependencies in formulas
   */
  public detectCircularDependencies(): { hasCircular: boolean; cycles: string[][] } {
    if (!this.recipeGroup) return { hasCircular: false, cycles: [] };

    const cycles: string[][] = [];
    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    // Build dependency graph
    const graph = new Map<string, string[]>();

    for (const column of this.recipeGroup.columns) {
      if (column.sourceType === Source.Formula || column.sourceType === Source.OverwrittableFormula) {
        if (column.source && column.symbol) {
          const dependencies = this.getFormulaDependencies(column.source);
          graph.set(column.symbol, dependencies);
        }
      }
    }

    // Add runtime formula to dependency graph if it exists
    if (this.runtimeFormula) {
      const runtimeDependencies = this.getFormulaDependencies(this.runtimeFormula);
      graph.set('__runtime__', runtimeDependencies);
    }

    // Add FormulaPerRow formulas to dependency graph
    for (const row of this.recipeGroup.rows) {
      if (row.formulas && Array.isArray(row.formulas)) {
        for (const [colSymbol, formula] of row.formulas) {
          const dependencies = this.getFormulaDependencies(formula);
          graph.set(`${row.symbol}.${colSymbol}`, dependencies);
        }
      }
    }

    // DFS to detect cycles
    const dfs = (node: string, path: string[]): void => {
      if (recursionStack.has(node)) {
        // Found a cycle
        const cycleStart = path.indexOf(node);
        const cycle = path.slice(cycleStart);
        cycles.push([...cycle, node]);
        return;
      }

      if (visited.has(node)) return;

      visited.add(node);
      recursionStack.add(node);
      path.push(node);

      const dependencies = graph.get(node) || [];
      for (const dep of dependencies) {
        dfs(dep, path);
      }

      recursionStack.delete(node);
      path.pop();
    };

    // Check all nodes
    for (const node of graph.keys()) {
      if (!visited.has(node)) {
        dfs(node, []);
      }
    }

    return {
      hasCircular: cycles.length > 0,
      cycles,
    };
  }

  /**
   * Get only the values of columns that are actually referenced in a formula
   */
  public getFormulaRelevantValues(formula: string, rowValues: Map<string, any>): Record<string, any> {
    const dependencies = this.getFormulaDependencies(formula);
    const relevantValues: Record<string, any> = {};

    for (const dep of dependencies) {
      if (rowValues.has(dep)) {
        relevantValues[dep] = rowValues.get(dep);
      }
    }

    return relevantValues;
  }

  public apply(object?: RecipeGroupState | RecipeRowState): FormulaScope {
    const scope = new FormulaScope(() => this.recipe, this.const, this.runtimeFormula);
    // Copy existing state
    scope.recipeGroup = this.recipeGroup;
    scope.recipeRow = this.recipeRow;

    if (object && 'columns' in object) {
      const newGroup = object as RecipeGroupState;
      // Only rebuild dependency cache if group structure changed (symbol, columns, or formulas)
      const structureChanged =
        !scope.recipeGroup ||
        scope.recipeGroup.symbol !== newGroup.symbol ||
        scope.recipeGroup.columns.length !== newGroup.columns.length ||
        scope.recipeGroup.columns.some((col, index) => {
          const newCol = newGroup.columns[index];
          return (
            !newCol ||
            col.symbol !== newCol.symbol ||
            col.sourceType !== newCol.sourceType ||
            col.source !== newCol.source
          );
        });

      if (structureChanged) {
        scope.recipeGroup = newGroup;
        scope.buildDependencyCache();
      } else {
        // Update the group reference but keep existing cache
        scope.recipeGroup = newGroup;
      }
    } else if (object && 'values' in object) {
      scope.recipeRow = object as RecipeRowState;
    }
    return scope;
  }

  public cell(rowSymbol: string, columnSymbol: string): string | number | null {
    const group = this.recipeGroup || this.findGroup(this.recipe.groups[0]?.symbol);
    if (!group) return '';

    const row = this.findRow(group, rowSymbol);
    if (!row) return '';

    const value = this.getCellValue(group, row, columnSymbol);
    return value;
  }

  public duration(rowSymbol: string, columnSymbol: string, unit?: string): string | number | null {
    const group = this.recipeGroup || this.findGroup(this.recipe.groups[0]?.symbol);
    if (!group) return '';

    const row = this.findRow(group, rowSymbol);
    if (!row) return '';

    const value = this.getCellValue(group, row, columnSymbol);
    if (typeof value === 'string' && value.includes(':')) {
      // Parse time format like "1:30:00" to minutes
      const parts = value.split(':').map(Number);
      if (parts.length === 3) {
        const minutes = parts[0] * 60 + parts[1] + parts[2] / 60;
        return unit === 'hours' ? minutes / 60 : minutes;
      }
    }
    return value;
  }

  public group(
    groupSymbol: string,
    columnSymbol: string,
    rowSymbol?: string
  ): string | number | null | Array<string | number | null> {
    const group = this.findGroup(groupSymbol);
    if (!group) {
      // Return empty array for missing groups (common in conditional recipes)
      // This allows sum(group(...)) to return 0 instead of null
      if (!rowSymbol) {
        log.debug('[FormulaScope] group() - group not found, returning empty array:', () => ({
          groupSymbol,
          columnSymbol,
        }));
        return [];
      }
      return null;
    }

    if (rowSymbol) {
      const row = this.findRow(group, rowSymbol);
      if (!row) return null;
      const value = this.getCellValue(group, row, columnSymbol);

      return value;
    } else {
      // Return array of all values in the column
      const values = group.rows.map((row) => this.getCellValue(group, row, columnSymbol));
      return values;
    }
  }

  public lookup(field: string, column: string, rowSymbol?: string, groupSymbol?: string): string | number {
    const group = groupSymbol ? this.findGroup(groupSymbol) : this.recipeGroup;
    if (!group) {
      log.warn(`[FormulaScope] Lookup failed: group not found for symbol ${groupSymbol}`);
      return '';
    }

    const row = rowSymbol ? this.findRow(group, rowSymbol) : this.recipeRow;
    if (!row) {
      log.warn(`[FormulaScope] Lookup failed: row not found for symbol ${rowSymbol}`);
      return '';
    }

    const columnDef = group.columns.find((col) => col.symbol === column);
    if (!columnDef || !columnDef.isLookup) {
      log.warn(`[FormulaScope] Lookup failed: column ${column} not found or not a lookup column`);
      return '';
    }

    const lookupValue = row.values.get(column);
    if (!lookupValue) {
      log.warn(`[FormulaScope] Lookup failed: no value found for column ${column}`);
      return '';
    }

    // Find the lookup object
    const lookupObj = columnDef.dropdownValues.find((item) => {
      if (typeof item === 'object' && item !== null) {
        // Try both exact match and string conversion
        const itemId = item.id;
        const lookupValueStr = String(lookupValue);
        return itemId === lookupValue || itemId === lookupValueStr;
      }
      return false;
    });

    if (!lookupObj || typeof lookupObj !== 'object') {
      log.warn(`[FormulaScope] Lookup failed: no matching object found for value ${lookupValue}`);
      return '';
    }

    log.debug(`[FormulaScope] Lookup found object:`, () => ({
      id: (lookupObj as any).id,
      label: (lookupObj as any).label,
      hasField: field in lookupObj,
      objectKeys: Object.keys(lookupObj),
    }));

    // Navigate the field path (e.g., "level1.level2")
    const fieldPath = field.split('.');
    let result: any = lookupObj;

    for (const path of fieldPath) {
      if (result && typeof result === 'object' && path in result) {
        result = result[path];
      } else {
        // Only warn if the path doesn't exist, not if it's undefined/null
        log.warn(`[FormulaScope] Lookup failed: field path ${field} not found in object. Available keys:`, () =>
          result && typeof result === 'object' ? Object.keys(result) : 'not an object'
        );
        return '';
      }
    }

    // Return the value even if it's undefined or null - that's valid data
    return result ?? '';
  }

  public uploadedValue(colHeader: string, keyColheader: string, rowKey: string): string | number {
    if (!this.uploadData) return '';

    const row = this.uploadData.find((r) => r[keyColheader] === rowKey);
    if (!row) return '';

    return row[colHeader] || '';
  }

  public sumUploadedColumn(colHeader: string): number {
    if (!this.uploadData) return 0;

    return this.uploadData.reduce((sum, row) => {
      const value = parseFloat(row[colHeader]);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  }

  public getColumn(columnSymbol: string): RecipeColumnState | undefined {
    if (!this.recipeGroup) return undefined;
    return this.recipeGroup.columns.find((col) => col.symbol === columnSymbol);
  }

  /**
   * Mathjs reserved keywords that cannot be used as variable names
   */
  private static readonly MATHJS_RESERVED = new Set([
    'end',
    'start',
    'in',
    'and',
    'not',
    'or',
    'xor',
    'to',
    'true',
    'false',
    'null',
    'undefined',
  ]);

  /**
   * Get column symbols as scope variables (for FormulaPerRow formulas)
   * Filters out mathjs reserved keywords to avoid conflicts
   */
  private getColumnSymbolsScope(): Record<string, any> {
    const columnScope: Record<string, any> = {};

    if (!this.recipeGroup) return columnScope;

    // Add all column symbols to scope, even if they don't have values yet
    // Skip mathjs reserved keywords to avoid conflicts
    for (const column of this.recipeGroup.columns) {
      if (column.symbol && !FormulaScope.MATHJS_RESERVED.has(column.symbol)) {
        // Initialize with null if no value exists yet - don't assume 0
        columnScope[column.symbol] = null;
      }
    }

    return columnScope;
  }

  private findGroup(symbol: string): RecipeGroupState | undefined {
    return this.recipe.groups.find((g: RecipeGroupState) => g.symbol === symbol);
  }

  private findRow(group: RecipeGroupState, symbol?: string): RecipeRowState | undefined {
    if (!symbol) return this.recipeRow;
    return group.rows.find((r) => r.symbol === symbol);
  }

  private getCellValue(group: RecipeGroupState, row: RecipeRowState, columnSymbol: string): string | number | null {
    const column = group.columns.find((col) => col.symbol === columnSymbol);
    if (!column) return null;

    // Check if it's a calculated column
    if (column.sourceType === Source.Formula || column.sourceType === Source.OverwrittableFormula) {
      // CRITICAL: First check if there's a stored calculated value
      // This prevents re-evaluating formulas with stale dependencies during cross-group calculations
      const storedValue = row.values.get(columnSymbol);
      if (storedValue !== undefined && storedValue !== null && storedValue !== '') {
        // Use the stored calculated value
        return typeof storedValue === 'number' ? storedValue : parseFloat(String(storedValue));
      }

      // No stored value - evaluate the formula
      if (column.source) {
        try {
          const result = this.evaluateFormula(column.source, row);
          // Convert boolean results to string, arrays to null (not supported in cell values)
          if (typeof result === 'boolean') return result.toString();
          if (Array.isArray(result)) return null;
          return result;
        } catch (error) {
          log.warn(`[FormulaScope] Formula evaluation error for ${columnSymbol}:`, error);
          return '';
        }
      }
    }

    // Check if it's a FormulaPerRow column
    if (column.sourceType === Source.FormulaPerRow) {
      if (row.formulas && Array.isArray(row.formulas)) {
        // Find the formula for this column
        const rowFormula = row.formulas.find(([colSymbol]) => colSymbol === columnSymbol);
        if (rowFormula && rowFormula[1]) {
          try {
            const result = this.evaluateFormula(rowFormula[1], row);
            // Convert boolean results to string, arrays to null (not supported in cell values)
            if (typeof result === 'boolean') return result.toString();
            if (Array.isArray(result)) return null;
            return result;
          } catch (error) {
            log.warn(`[FormulaScope] FormulaPerRow evaluation error for ${columnSymbol}:`, error);
            return '';
          }
        }
      }
    }

    // Return the stored value - convert empty strings to null to propagate through calculations
    const value = row.values.get(columnSymbol);
    if (value === '' || value === null || value === undefined) return null;

    // Handle arrays (multi-select values) - convert to null for formula evaluation
    if (Array.isArray(value)) {
      return null;
    }

    // Convert numeric strings to numbers for better formula evaluation
    if (typeof value === 'string' && !isNaN(Number(value))) {
      return Number(value);
    }

    return value;
  }

  public evaluateFormula(
    formula: string,
    row: RecipeRowState
  ): string | number | boolean | null | Array<string | number | null> {
    // Create a single scope for formula evaluation with all functions and variables
    const scope = {
      ...this.const,
      // Add constants under 'const' name for bracket notation access (e.g., const[elem])
      const: this.const,
      // Add column symbols to scope even if they don't have values yet
      ...this.getColumnSymbolsScope(),
      // Add current row values for FormulaPerRow formulas that reference columns directly
      // Convert empty strings to null to propagate through calculations
      // Filter out mathjs reserved keywords to avoid conflicts
      ...Object.fromEntries(
        Array.from(row.values.entries())
          .filter(([key]) => !FormulaScope.MATHJS_RESERVED.has(key))
          .map(([key, value]) => [key, value === '' || value === null || value === undefined ? null : value])
      ),
      // Add all the custom functions
      cell: (rowSymbol: string, colSymbol: string) => this.cell(rowSymbol, colSymbol),
      group: (groupSymbol: string, colSymbol: string, rowSymbol?: string) =>
        this.group(groupSymbol, colSymbol, rowSymbol),
      lookup: (field: string, column: string, rowSymbol?: string, groupSymbol?: string) =>
        this.lookup(field, column, rowSymbol, groupSymbol),
      duration: (rowSymbol: string, colSymbol: string, unit?: string) => this.duration(rowSymbol, colSymbol, unit),
      uploadedValue: (colHeader: string, keyColheader: string, rowKey: string) =>
        this.uploadedValue(colHeader, keyColheader, rowKey),
      sumUploadedColumn: (colHeader: string) => this.sumUploadedColumn(colHeader),
      // Add current row context
      currentRow: row,
      currentGroup: this.recipeGroup,
      // Add standard math functions
      abs,
      round,
      floor,
      ceil,
      min,
      max,
      sqrt,
      pow,
      exp,
      log: mathLog,
      sin,
      cos,
      tan,
      PI: pi,
      E: e,
      // Add utility functions for arrays
      sum: (arr: any[]) => {
        if (!Array.isArray(arr)) {
          log.warn('[FormulaScope] sum() called with non-array:', () => ({ arr }));
          return null;
        }
        const validValues = arr.filter((v) => v !== null && v !== undefined && v !== '');
        if (validValues.length === 0) {
          return null;
        }
        const result = validValues.reduce((acc, v) => {
          const num = typeof v === 'number' ? v : parseFloat(v);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
        return result;
      },
      // Add mathjs unit conversion functions
      unit: (value: any, unitStr: string) => unit(value, unitStr),
      number: (value: any, targetUnit?: string) => {
        // mathjs number() can convert units or just extract numeric value
        if (targetUnit) {
          return mathNumber(value, targetUnit);
        }
        return typeof value === 'number' ? value : parseFloat(String(value));
      },
      // Keep these for backwards compatibility (simple passthrough)
      hours: (value: number) => value,
      days: (value: number) => value,
      minutes: (value: number) => value,
      // Add comparison functions for string comparisons
      eq: (a: any, b: any) => Boolean(a === b),
      ne: (a: any, b: any) => Boolean(a !== b),
      avg: (arr: any[]) => {
        if (!Array.isArray(arr) || arr.length === 0) return 0;
        const validValues = arr.filter((v) => v !== null && v !== undefined && v !== '');
        if (validValues.length === 0) return 0;
        const sum = validValues.reduce((acc, v) => {
          const num = typeof v === 'number' ? v : parseFloat(v);
          return acc + (isNaN(num) ? 0 : num);
        }, 0);
        return sum / validValues.length;
      },
      count: (arr: any[]) => {
        if (!Array.isArray(arr)) return 0;
        return arr.filter((v) => v !== null && v !== undefined && v !== '').length;
      },
    };

    const result = this.evaluateMathExpression(formula, scope);

    return result;
  }

  private evaluateMathExpression(
    expression: string,
    scope: any
  ): string | number | boolean | null | Array<string | number | null> {
    try {
      // Handle unit conversion patterns like "4 hours in days" or "t_h hours in days"
      // Support both literal numbers and variable names
      const unitConversionMatch = expression.match(/^([\w.]+)\s+hours?\s+in\s+days?$/i);
      if (unitConversionMatch) {
        const valueStr = unitConversionMatch[1];
        // Try to parse as number, otherwise evaluate as variable
        const hours = isNaN(Number(valueStr))
          ? typeof scope[valueStr] === 'number'
            ? scope[valueStr]
            : parseFloat(String(scope[valueStr]))
          : parseFloat(valueStr);
        return isNaN(hours) ? null : hours / 24;
      }

      const unitConversionMatch2 = expression.match(/^([\w.]+)\s+hours?\s+in\s+minutes?$/i);
      if (unitConversionMatch2) {
        const valueStr = unitConversionMatch2[1];
        // Try to parse as number, otherwise evaluate as variable
        const hours = isNaN(Number(valueStr))
          ? typeof scope[valueStr] === 'number'
            ? scope[valueStr]
            : parseFloat(String(scope[valueStr]))
          : parseFloat(valueStr);
        return isNaN(hours) ? null : hours * 60;
      }

      // Evaluate the expression using mathjs - the scope already contains all functions and variables
      const result = evaluate(expression, scope);

      // Check if any operands were null - if so, propagate null
      if (result === null || result === undefined) {
        return null;
      }

      // Return the result, handling different types
      if (typeof result === 'number') {
        return result;
      } else if (typeof result === 'string') {
        return result;
      } else if (typeof result === 'boolean') {
        return result;
      } else if (Array.isArray(result)) {
        return result;
      } else {
        return String(result);
      }
    } catch (error) {
      log.warn(
        '[FormulaScope] Math evaluation failed:',
        error,
        'Expression:',
        expression,
        'Scope keys:',
        Object.keys(scope)
      );
      return null;
    }
  }
}
